import { useSyncExternalStore } from "react";
import { formatLongDate } from "../lib/format";
import { boardIsStale } from "../site-data";
import { clockServerSnapshot, clockSnapshot, subscribeToClock } from "../lib/clock";
import styles from "./BoardDateline.module.css";

/**
 * The board's dateline, and the stale-board guard that corrects it (`WEB-043`, canon
 * decisions `D-015` § 3 and `D-019`).
 *
 * The line states two facts about the board below it: which display day it is, and that the
 * day turns at 00:00 UTC. The homepage is built rather than read live, so the board is
 * refreshed by a build at the auction boundary — and if that build does not run, both facts
 * stop being true together. The page goes on printing an earlier day's `displayDate` while
 * saying the board resets at a boundary that has already passed, which reads as a confident
 * claim about today.
 *
 * So the correction replaces the second clause rather than adding a third thing to the page:
 * the date still says which day, and where "resets 00:00 UTC" would have been, the line says
 * today's board is not published yet. `D-019` moved this here from a full-width banner above
 * the board, on the operator's objection — the banner narrated Dealbid's build pipeline to a
 * buyer who has no use for it, and it was the loud answer to the quiet question `D-015` § 7
 * had already answered quietly for the click-ranked lists.
 *
 * What it must still do is unchanged: a buyer can tell which display day they are looking at,
 * and cannot read it as today's.
 *
 * The check costs no request, which is what let `D-015` put it on the page rather than behind
 * a fetch. Canon § 8 already makes the display day UTC-canonical, so "is the built date before
 * today's UTC date" is a comparison the browser can make alone.
 *
 * It is deliberately **one-directional**: a built date *after* today's is a build that ran
 * seconds before the boundary and is about to be right, not a stale one. Reporting that would
 * put a warning on the page every night.
 *
 * Subscribed to the shared clock rather than sampled once, so a page left open across 00:00
 * UTC starts saying this rather than continuing to present an earlier day as today.
 *
 * **What prerendering costs this guard, stated rather than discovered (`WEB-044`, `D-020`).**
 * The page is rendered to HTML at build time, and a build cannot know when it will be read.
 * So the shipped HTML is written against the build's own clock — at which point the board it
 * was built with is current by construction, and the correction is absent. The browser
 * recomputes on hydration and the correction appears, which is the whole of the guard for
 * anyone running JavaScript.
 *
 * A reader with JavaScript disabled therefore does not get it. That is a real loss and a
 * bounded one: it needs the boundary build to have failed *and* the reader to have scripting
 * off. The alternative — computing staleness at build time — would freeze the comparison at
 * build time and be wrong for everybody, always, which is strictly worse. `OPS-011`'s
 * operator-side alarm is the half that does not depend on the reader at all. This is
 * also why the subscription lives in this small component and not in `TodayBoard`: the clock
 * ticks every second, and the board is the largest section on the page.
 */
export function BoardDateline({ displayDate }: { displayDate: string | null }) {
  const nowMs = useSyncExternalStore(subscribeToClock, clockSnapshot, clockServerSnapshot);
  const stale = displayDate !== null && boardIsStale(displayDate, nowMs);

  /*
   * The live region is rendered in both states, not only the stale one. A live region that
   * is inserted into the DOM at the moment its news arrives is announced unreliably; one
   * that is already there when its text changes is announced. Since the change this has to
   * announce happens at 00:00 UTC on a page nobody reloaded, that distinction is the whole
   * point of having the role at all.
   */
  return (
    <>
      {displayDate === null ? null : `${formatLongDate(displayDate)} · `}
      <span role="status" className={stale ? styles.stale : undefined}>
        {stale
          ? "today’s board is not published yet"
          : displayDate === null
            ? "Resets 00:00 UTC"
            : "resets 00:00 UTC"}
      </span>
    </>
  );
}
